import { useCallback, useMemo } from 'react';
import classnames from 'classnames/bind';
import { FormField, Indication, RangeSlider } from 'taboola-ultimate-ui';
import { useAvailableItems } from 'hooks';
import { SupplyTargetingIcons } from 'modules/campaigns/modules/common-campaign-form/components/SupplyTargeting/components/SupplyTargetingIcons/SupplyTargetingIcons';
import { supplyTargetingSliderOptions } from 'modules/campaigns/modules/common-campaign-form/components/SupplyTargeting/const';
import { useAPISupplyTargetingRestrictions } from 'modules/campaigns/modules/common-campaign-form/components/SupplyTargeting/hooks/useAPISupplyTargetingRestrictions';
import { useSupplyTargetingFormField } from 'modules/campaigns/modules/common-campaign-form/components/SupplyTargeting/hooks/useSupplyTargetingFormField';
import { FormattedMessage, useIntl } from 'modules/taboola-common-frontend-modules/i18n';
import styles from './SupplyTargetingSlider.module.scss';

const classNameBuilder = classnames.bind(styles);

const MIN_SCROLL = 0;
const STEP_SCROLL = 1;

export const SupplyTargetingSlider = () => {
    const { formatMessage } = useIntl();
    const { value: campaignSupplyTargeting, onChange } = useSupplyTargetingFormField();
    const { supplyTargetingResource, isSupplyPermitted } = useAPISupplyTargetingRestrictions();
    const availableOptions = useAvailableItems(supplyTargetingSliderOptions, {
        supplyTargetingResource,
        campaignSupplyTargeting,
    });
    const MAX_SCROLL = availableOptions.length - 1;
    const availableSupplyTargetingOptions = useMemo(() => {
        return availableOptions.reduce((acc, item, index) => {
            acc[index] = item;
            return acc;
        }, []);
    }, [availableOptions]);
    const selectedValueId = useMemo(
        () =>
            (availableSupplyTargetingOptions || []).findIndex(
                supplyTargeting => supplyTargeting.value === campaignSupplyTargeting
            ),
        [campaignSupplyTargeting, availableSupplyTargetingOptions]
    );

    const changeHandler = useCallback(
        (event, newValue) => {
            onChange(availableSupplyTargetingOptions[newValue].value);
        },
        [onChange, availableSupplyTargetingOptions]
    );

    if (!isSupplyPermitted) {
        return null;
    }

    return (
        <FormField
            inputId="supply-targeting"
            label={
                <FormattedMessage
                    id="campaign.editor.targeting.supplyTargeting.title"
                    defaultMessage="Supply Targeting"
                />
            }
            containerClass={styles['input']}
        >
            <div className={styles['slider-container']}>
                <div className={styles['slider-wrapper']}>
                    <div className={classNameBuilder('slider-label-start')}>
                        <span>
                            <FormattedMessage
                                id={`campaign.editor.targeting.supply.slider.title.${
                                    availableSupplyTargetingOptions[MIN_SCROLL]?.id ?? ''
                                }`}
                            />
                        </span>
                    </div>
                    <RangeSlider
                        value={selectedValueId}
                        min={MIN_SCROLL}
                        max={MAX_SCROLL}
                        step={STEP_SCROLL}
                        marks
                        onChange={changeHandler}
                        aria-labelledby="supply-targeting-slider"
                        valueLabelFormat={() =>
                            formatMessage({
                                id: `campaign.editor.targeting.supply.slider.${campaignSupplyTargeting}`,
                            })
                        }
                        style={{ fontSize: '10px' }}
                    />
                    <div className={classNameBuilder('slider-label-end')}>
                        <FormattedMessage
                            id={`campaign.editor.targeting.supply.slider.title.${
                                availableSupplyTargetingOptions[MAX_SCROLL]?.id ?? ''
                            }`}
                        />
                    </div>
                </div>

                {availableSupplyTargetingOptions[selectedValueId] && (
                    <div className={styles['indication-wrapper']}>
                        <Indication
                            type="info"
                            displayDismissComponent={false}
                            message={
                                <div>
                                    <FormattedMessage
                                        id={`campaign.editor.targeting.supply.slider.title.${campaignSupplyTargeting}`}
                                    />
                                    <span>: </span>
                                    <FormattedMessage
                                        id={`campaign.editor.targeting.supply.slider.subtext.${campaignSupplyTargeting}`}
                                    />
                                    <div className={styles['divider']} />
                                    <SupplyTargetingIcons
                                        supplyTargeting={availableSupplyTargetingOptions[selectedValueId]}
                                    />
                                </div>
                            }
                        />
                    </div>
                )}
            </div>
        </FormField>
    );
};
